import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useForm } from 'react-hook-form';

import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';

import StaffDwrForm from './StaffDwrForm';

import {
  Backdrop,
  CircularProgress,
} from '@mui/material';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import config from 'src/config';


dayjs.extend(utc);
dayjs.extend(timezone);


const { PRIVATE_ROUTE } = API_ROUTES;


function StaffDwrCreateForm() {
  const { id } = useParams();
  const { authAxios } = useContext(FetchContext);
  const navigate = useNavigate();


  const [loading, setLoading] = useState(false);

  const [taskdata, setTaskData] = useState([]);

  const [state, setState] = useState();

  const [attachments, setAttachments] =
    useState('');

  const [signOne, setSignOne] =
    useState('');

  const [signTwo, setSignTwo] =
    useState('');

  const [mySignature, setMySignature] =
    useState('');


  const [jobIdValue, setJobIdValue] =
    useState('');


  /*
   * Complete available-work response.
   *
   * This is the ONLY source for:
   *
   * Job
   * Task
   * Available Work cards
   */
  const [availableWork, setAvailableWork] =
    useState([]);


  const [selectedWork, setSelectedWork] =
    useState(null);


  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isSubmitting,
    },
    setValue,
    getValues,
    watch,
    control,
  } = useForm({
    defaultValues: {
      task_name: '',
      task_hour: 0,
      description: '',

      representative_sign: '',

      client_representative_sign: '',

      task_date: '',

      client_representative: '',

      client_approved_DWR: '',

      remark: '',

      submit_status: true,

      job: '',

      job_id: '',

      task_id: '',

      project_managers_array: [],

      project_manager_id: '',
    },
  });


  const selectedDate = watch('task_date');


  /*
   * ============================================================
   * Get logged-in employee
   * ============================================================
   *
   * No /api/user request.
   *
   * Employee ID comes directly from localStorage.
   */

  const getCurrentEmployeeId =
    useCallback(() => {
      try {
        const userData =
          JSON.parse(
            localStorage.getItem(
              config.PROJECT_NAME
            )
          );

        return (
          userData?.user?._id || null
        );
      } catch (error) {
        console.error(
          'Unable to read logged-in user:',
          error
        );

        return null;
      }
    }, []);


  /*
   * ============================================================
   * Normalize available-work response
   * ============================================================
   */

  const normalizeAvailableWork =
    useCallback((responseData) => {
      /*
       * If backend directly returns an array.
       */
      if (Array.isArray(responseData)) {
        return responseData;
      }

      /*
       * Backend response:
       *
       * {
       *   schedules: [],
       *   todoDrafts: []
       * }
       */
      const schedules =
        Array.isArray(responseData?.schedules)
          ? responseData.schedules
          : [];

      const todoDrafts =
        Array.isArray(responseData?.todoDrafts)
          ? responseData.todoDrafts
          : [];

      /*
       * Logged-in employee.
       */
      const employeeId =
        getCurrentEmployeeId();

      /*
       * ============================================================
       * FILTER SCHEDULES
       * ============================================================
       *
       * Backend intentionally returns schedules with linked DWRs.
       *
       * Available DWR UI should NOT show a schedule that already
       * has a DWR for the logged-in employee.
       *
       * Rejected DWRs remain available for resubmission.
       */
      const availableSchedules =
        schedules.filter((schedule) => {
          const employeeAssignment =
            schedule?.assigned_members?.find(
              (member) =>
                String(member?.employee) ===
                String(employeeId) ||
                String(
                  member?.employee_data?._id
                ) === String(employeeId)
            );

          /*
           * Employee is not assigned to this schedule.
           */
          if (!employeeAssignment) {
            return false;
          }

          /*
           * No DWR exists for this employee.
           * Show the schedule.
           */
          if (!employeeAssignment?.dwr_id) {
            return true;
          }

          /*
           * Rejected DWR can be submitted again.
           */
          if (
            employeeAssignment?.dwr_status
              ?.toLowerCase() === 'rejected'
          ) {
            return true;
          }

          /*
           * Pending / Approved / Open DWR:
           * do not show the schedule in Available Work.
           */
          return false;
        });

      /*
       * ============================================================
       * NORMALIZE
       * ============================================================
       */

      return [
        ...availableSchedules.map(
          (schedule) => ({
            type: 'schedule',

            schedule,

            todoDraft: null,

            job_id:
              schedule?.job_id,

            task_id:
              schedule?.task_id,

            task_number:
              schedule?.task_number,
          })
        ),

        /*
         * TodoDrafts are independent from schedules.
         *
         * Do not filter them based on schedule DWR information.
         */
        ...todoDrafts.map(
          (draft) => ({
            type: 'todoDraft',

            schedule: null,

            todoDraft: draft,

            job_id:
              draft?.job_id,

            task_id:
              draft?.task_id,

            task_number:
              draft?.task_number,
          })
        ),
      ];
    }, [
      getCurrentEmployeeId,
    ]);


  /*
   * ============================================================
   * Get available work
   * ============================================================
   *
   * IMPORTANT:
   *
   * This is the ONLY API call used to discover
   * available Job / Task / Work.
   *
   * Date + employee.
   */

  const getAvailableWork =
    useCallback(
      async (taskDate) => {
        if (!taskDate) {
          setAvailableWork([]);

          return;
        }


        const employeeId =
          getCurrentEmployeeId();


        if (!employeeId) {
          toast.error(
            'Unable to identify the logged-in employee.'
          );

          setAvailableWork([]);

          return;
        }


        try {
          setLoading(true);


          const response =
            await authAxios.get(
              `${PRIVATE_ROUTE.GET_DWR_AVAILABLE_WORK
              }?task_date=${encodeURIComponent(
                taskDate
              )}&employee_id=${encodeURIComponent(
                employeeId
              )}`
            );


          if (
            response?.data?.statusCode !==
            200
          ) {
            setAvailableWork([]);

            return;
          }


          const normalized =
            normalizeAvailableWork(
              response?.data?.data
            );


          setAvailableWork(normalized);


          /*
           * Reset Job / Task whenever a new
           * date response is loaded.
           */

          setJobIdValue('');

          setValue(
            'job_id',
            ''
          );

          setValue(
            'job',
            ''
          );

          setValue(
            'task_id',
            ''
          );

          setValue(
            'task_name',
            ''
          );

          setSelectedWork(null);
        } catch (error) {
          console.error(
            'Error fetching available DWR work:',
            error
          );

          setAvailableWork([]);

          toast.error(
            'Unable to load available work.'
          );
        } finally {
          setLoading(false);
        }
      },
      [
        authAxios,
        PRIVATE_ROUTE.GET_DWR_AVAILABLE_WORK,
        getCurrentEmployeeId,
        normalizeAvailableWork,
        setValue,
      ]
    );


  /*
   * ============================================================
   * Date changed
   * ============================================================
   *
   * React-hook-form watches task_date.
   *
   * Therefore:
   *
   * Date selected
   *       ↓
   * getAvailableWork(date)
   *       ↓
   * ONE API request
   */

  useEffect(() => {
    if (!id) {
      if (selectedDate) {
        getAvailableWork(
          selectedDate
        );
      } else {
        setAvailableWork([]);

        setJobIdValue('');

        setSelectedWork(null);
      }
    }
  }, [
    id,
    selectedDate,
    getAvailableWork,
  ]);


  /*
   * ============================================================
   * Existing DWR
   * ============================================================
   *
   * Only used when editing.
   *
   * The create workflow does NOT use this.
   */

  const getDWRFormById =
    useCallback(async () => {
      if (!id) {
        return;
      }


      try {
        const { data } =
          await authAxios.get(
            `${PRIVATE_ROUTE.GET_DWR_BY_ID}/${id}`
          );


        const dwrData =
          data?.data?.[0];


        if (!dwrData) {
          return;
        }


        setState(dwrData);


        setJobIdValue(
          dwrData?.taskdata?.job_id
        );


        setValue(
          'task_name',
          dwrData?.taskdata?.number_str
        );


        setValue(
          'task_id',
          dwrData?.task_id
        );


        setValue(
          'job_id',
          dwrData?.job_id
        );


        setValue(
          'job',
          dwrData?.taskdata?.job_id
        );


        setValue(
          'project_manager_id',
          dwrData?.task_id
        );


        setValue(
          'project_managers_array',
          dwrData?.project_manager
        );


        setValue(
          'client_representative',
          dwrData?.client_representative
        );


        setValue(
          'client_representative_id',
          dwrData?.client_representative
        );


        setValue(
          'task_date',
          dayjs(
            dwrData?.task_date
          ).format('YYYY-MM-DD')
        );


        setValue(
          'task_hour',
          dwrData?.task_hour
        );


        setValue(
          'representative_sign',
          dwrData?.representative_sign
        );


        setValue(
          'client_representative_sign',
          dwrData?.client_representative_sign
        );


        setValue(
          'client_approved_DWR',
          dwrData?.client_approved_DWR
        );


        setValue(
          'submit_status',
          dwrData?.submit_status
        );


        setValue(
          'remark',
          dwrData?.remark
        );


        setAttachments(
          dwrData?.client_approved_DWR
        );


        setMySignature('');


        setSignOne(
          dwrData?.representative_sign
        );


        setSignTwo(
          dwrData?.client_representative_sign
        );
      } catch (error) {
        console.error(
          'Error fetching DWR:',
          error
        );

        toast.error(
          'Failed to load DWR report'
        );
      }
    }, [
      authAxios,
      id,
      setValue,
      PRIVATE_ROUTE.GET_DWR_BY_ID,
    ]);


  /*
   * ============================================================
   * Initial loading
   * ============================================================
   */

  useEffect(() => {
    const loadPage =
      async () => {
        try {
          setLoading(true);


          if (id) {
            await getDWRFormById();
          }
        } catch (error) {
          console.error(
            'Error loading DWR page:',
            error
          );
        } finally {
          setLoading(false);
        }
      };


    loadPage();
  }, [
    id,
    getDWRFormById,
  ]);


  /*
   * ============================================================
   * Submit
   * ============================================================
   */

  const onSubmit = async (data) => {
    try {
      const userData =
        JSON.parse(
          localStorage.getItem(
            config.PROJECT_NAME
          )
        );


      const newData = {
        ...data,

        task_id:
          data?.task_id?.trim(),

        job_id:
          data?.job_id?.trim(),

        user_id:
          userData?.user?._id,
      };


      if (id) {
        const response =
          await authAxios.post(
            `${PRIVATE_ROUTE.EDIT_DWR}${id}`,
            newData
          );


        if (
          response?.data
            ?.statusCode === 200
        ) {
          toast.success(
            response?.data?.massage
          );

          navigate(
            '/staff/dwr/list'
          );
        } else {
          toast.error(
            response?.data?.massage
          );
        }
      } else {
        const response =
          await authAxios.post(
            PRIVATE_ROUTE.CREATE_DWR,
            newData
          );


        if (
          response?.data
            ?.statusCode === 200
        ) {
          toast.success(
            response?.data?.massage
          );

          navigate(
            '/staff/dwr/list'
          );
        } else {
          toast.error(
            response?.data?.massage
          );
        }
      }
    } catch (error) {
      console.error(
        'Error submitting DWR:',
        error
      );

      toast.error(
        'Failed to submit DWR'
      );
    }
  };


  /*
   * ============================================================
   * Render
   * ============================================================
   */

  return (
    <>
      {(loading ||
        isSubmitting) && (
          <Backdrop
            sx={{
              color: '#fff',

              zIndex: (
                theme
              ) =>
                theme.zIndex
                  .drawer + 1,
            }}
            open={
              loading ||
              isSubmitting
            }
          >
            <CircularProgress
              color="inherit"
            />
          </Backdrop>
        )}


      <StaffDwrForm
        getValues={
          getValues
        }

        register={
          register
        }

        watch={
          watch
        }

        handleSubmit={
          handleSubmit
        }

        errors={
          errors
        }

        onSubmit={
          onSubmit
        }

        params={
          id
        }

        loading={
          isSubmitting
        }

        isEdit={
          Boolean(id)
        }

        control={
          control
        }

        setValue={
          setValue
        }

        taskdata={
          taskdata
        }

        editdata={
          state
        }

        attachments={
          attachments
        }

        setAttachments={
          setAttachments
        }

        signOne={
          signOne
        }

        setSignOne={
          setSignOne
        }

        signTwo={
          signTwo
        }

        setSignTwo={
          setSignTwo
        }

        mySignature={
          mySignature
        }

        setMySignature={
          setMySignature
        }

        authAxios={
          authAxios
        }

        PRIVATE_ROUTE={
          PRIVATE_ROUTE
        }

        jobIdValue={
          jobIdValue
        }

        setJobIdValue={
          setJobIdValue
        }

        setLoading={
          setLoading
        }

        /*
         * IMPORTANT:
         *
         * Pass the COMPLETE available-work
         * response.
         *
         * StaffDwrForm will locally derive:
         *
         * Job
         * Task
         * Cards
         */
        availableWork={
          availableWork
        }

        selectedWork={
          selectedWork
        }

        setSelectedWork={
          setSelectedWork
        }
      />
    </>
  );
}


export default StaffDwrCreateForm;
